import { Injectable, Injector, inject } from '@angular/core';
import { AdminUserProfileState } from '../services/admin-user-profile.state';
import { PermissionService } from '../services/permission.service';
import { AdminWebSocketService } from '../services/admin-websocket.service';
import { AdminNotificationService } from '../services/admin-notification.service';
import { AdminRbacLiveSyncService } from '../services/admin-rbac-live-sync.service';

const TOKEN_KEY = 'admin_access_token';
const USER_KEY = 'admin_user';
const TENANT_KEY = 'admin_tenant';

@Injectable({ providedIn: 'root' })
export class AdminAuthStateService {
  private readonly profile = inject(AdminUserProfileState, { optional: true });
  private readonly permissionService = inject(PermissionService);

  /**
   * The websocket / notification / live-sync services are resolved lazily inside `logout()`
   * rather than injected as fields.
   *
   * Injecting them eagerly closes a DI cycle and Angular fails with NG0200 before the app can
   * even run the auth guard:
   *   AdminAuthStateService -> AdminWebSocketService -> AuthSessionRedirectService
   *                         -> AdminAuthStateService
   * They are only ever needed at logout, so deferring resolution to call time is both correct
   * and enough to break the cycle.
   */
  private readonly injector = inject(Injector);

  saveSession(token: string, user: object, tenant: object): void {
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(USER_KEY, JSON.stringify(user));
    localStorage.setItem(TENANT_KEY, JSON.stringify(tenant));
    this.permissionService.resetForNewSession();
  }

  getToken(): string | null {
    return localStorage.getItem(TOKEN_KEY);
  }

  isLoggedIn(): boolean {
    return !!this.getToken();
  }

  getUser(): { id: number; email: string | null; full_name: string | null } | null {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? JSON.parse(raw) : null;
  }

  getTenant(): { id: number; name: string | null } | null {
    const raw = localStorage.getItem(TENANT_KEY);
    return raw ? JSON.parse(raw) : null;
  }

  logout(): void {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(TENANT_KEY);
    this.profile?.clear();
    this.permissionService.clearPermissions();
    this.injector.get(AdminRbacLiveSyncService).stop();
    this.injector.get(AdminNotificationService).resetForLogout();
    this.injector.get(AdminWebSocketService).disconnect();
  }
}
